/**
 * THE SIMULATOR PALETTE: one declaration, every consumer imports it.
 *
 * <h2>Why a module and not five literals in five files</h2>
 *
 * This codebase has already paid for the alternative. Three files each held their own
 * `'#d4a017'` under a comment claiming it was shared, and the claim was true only until somebody
 * edited one — `forceFutureMarkerStyle` records that, and it runs LAST, so it would have won.
 *
 * <p>A palette is exactly the kind of thing that drifts: it is edited for appearance, by eye, one
 * file at a time. Here the hexes exist once and `simulatorPalette.test.js` pins them, so a change
 * is a decision somebody makes rather than a divergence somebody introduces.
 *
 * <h2>The specified palette</h2>
 *
 * <pre>
 *   PTA         #F59E0B   amber / goldenrod     pillars and range bounds
 *   PTB         #06B6D4   sky blue / cyan       pillars and range bounds
 *   CTS         #8B5CF6   royal purple          celestial timing bands
 *   algo buy    #10B981   emerald, SOLID
 *   algo sell   #EF4444   crimson, SOLID
 *   human buy   #00F0FF   neon cyan, HOLLOW
 *   human sell  #F43F5E   magenta, HOLLOW
 * </pre>
 *
 * <h2>⚠ PTA AMBER AND THE PLAYHEAD AMBER ARE NEARLY THE SAME HUE</h2>
 *
 * `PLAYHEAD_COLOR` is `#ffb300`, chosen because green and red already mean up and down on a
 * candlestick chart. PTA is now `#F59E0B`. They are about twelve degrees apart.
 *
 * <p>The playhead is the ONE mark a reader has to find instantly, so this is a real cost of the
 * specified palette and it is recorded rather than quietly worked around. What keeps them apart in
 * practice is everything other than hue: the playhead is 2px at 0.95 opacity with a state label
 * riding on it, while a PTA band is a 10%-opacity fill and its rays are 1.5px dotted/dashed with a
 * `PTA Top …` label. The playhead has NOT been recoloured, because it was not asked for and its
 * colour is documented and tested elsewhere; if the two prove hard to tell apart on a real chart,
 * moving the playhead to white is a one-line change in `replayPlayhead.js`.
 *
 * <h2>Scope: the SIMULATOR's surfaces, not every study that mentions a name</h2>
 *
 * The Critical Timing Scan's own up/down arrows keep their colours. Those are a customer-facing
 * study's direction encoding, not a simulator overlay, and repainting them would change a surface
 * nobody asked about. What takes `CTS` here is the replay's timing marks and pillars.
 */

/** PTA — amber / goldenrod. Vertical pillars, Top/Bottom/equator rays. */
export const PTA_COLOR = '#F59E0B';

/** PTB — sky blue / cyan. The same two surfaces, for the other band timeline. */
export const PTB_COLOR = '#06B6D4';

/** CTS — royal purple. Timing pillars and the per-bar timing marks. */
export const CTS_COLOR = '#8B5CF6';

/**
 * THE CTS TIMING MARK — one neutral amber, on a rim, for both canvases.
 *
 * <h2>No single yellow is legible on both, so the rim does the work</h2>
 *
 * Yellow is intrinsically light, so every clean one is invisible against a white chart. Measured
 * against this application's two canvases (`#f9fafe` and `#0d1117`):
 *
 * <pre>
 *   #FFD700 gold          1.34 : 1 on light     13.49 : 1 on dark
 *   #FFC107 amber         1.56                  11.61
 *   #FFB300               1.72                  10.54
 *   #B8860B dk goldenrod  3.12                   5.82   <- the only pair clearing 3:1, and brown
 * </pre>
 *
 * The only single values that clear the 3:1 floor on BOTH are dark goldenrods, which are not a
 * clean yellow — so a fill alone cannot satisfy the brief. The fill therefore stays one bright
 * amber in both themes, which is what makes the mark the same object wherever it is read, and an
 * INK RIM supplies the edge:
 *
 * <pre>
 *   on light   the rim is 18 : 1 against the canvas  -> the triangle is outlined, amber inside
 *   on dark    the rim is 1.03 : 1 against the canvas -> invisible, and the amber is 11.6 : 1
 * </pre>
 *
 * <p>That is the whole of it: the rim is doing work on exactly the canvas that needs it and
 * costs nothing on the one that does not, so no theme plumbing reaches the marker path.
 *
 * <p>⚠ {@link CTS_MARK_RIM} is the same ink `cycleColors` uses for the channel handle's core, and
 * is declared here rather than imported because `cycleColors` imports THIS module — the other
 * direction is a cycle. `ctsMarkStyle.test.js` pins the two equal.
 */
export const CTS_MARK_COLOR = '#FFC107';
export const CTS_MARK_RIM = '#0b0f14';

/** The engine's own fills: emerald in, crimson out. SOLID. */
export const ALGO_BUY_COLOR = '#10B981';
export const ALGO_SELL_COLOR = '#EF4444';

/** The operator's: neon cyan and magenta. HOLLOW — the silhouette is what says whose book. */
export const HUMAN_BUY_COLOR = '#00F0FF';
export const HUMAN_SELL_COLOR = '#F43F5E';

/**
 * Every colour the simulator draws with, for the tests that have to enumerate them.
 *
 * <p>A frozen map rather than a list, so a test can name what it is asserting about instead of
 * indexing into an array whose order is nobody's contract.
 */
export const SIMULATOR_PALETTE = Object.freeze({
  pta: PTA_COLOR,
  ptb: PTB_COLOR,
  cts: CTS_COLOR,
  algoBuy: ALGO_BUY_COLOR,
  algoSell: ALGO_SELL_COLOR,
  humanBuy: HUMAN_BUY_COLOR,
  humanSell: HUMAN_SELL_COLOR,
});

/** The structural categories, in the order the setup modal lists them. */
export const STRUCTURAL_COLORS = Object.freeze({
  PTA: PTA_COLOR,
  PTB: PTB_COLOR,
  CTS: CTS_COLOR,
});
